import React, { useState, useRef, useEffect } from 'react';

/**
 * AvatarMenu — top-right user avatar with a click-to-open dropdown.
 *
 * Props:
 *   initials:  string  — e.g. "EA"
 *   agentName: string? — displayed in the dropdown header
 *   agentEmail: string? — displayed under the name
 *   onGoToSettings: () => void
 *   onGoToAdmin:    () => void   (optional — renders "Owner Admin" item only when supplied)
 *   onSignOut:      () => void   (optional; defaults to a no-op toast)
 */
export default function AvatarMenu({
  initials = 'EA',
  agentName,
  agentEmail,
  onGoToSettings,
  onGoToAdmin,
  onOpenSupport,
  onSignOut,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onEsc(e) { if (e.key === 'Escape') setOpen(false); }
    if (open) {
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onEsc);
    }
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const handle = (fn) => {
    setOpen(false);
    if (typeof fn === 'function') fn();
  };

  return (
    <div className="eiq-avatar-menu" ref={wrapRef}>
      <button
        type="button"
        className={`eiq-avatar eiq-avatar-btn ${open ? 'open' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="avatar-menu-btn"
      >
        {initials}
      </button>
      {open && (
        <div className="eiq-avatar-dropdown" role="menu" data-testid="avatar-menu-dropdown">
          <div className="eiq-avatar-header">
            <div className="eiq-avatar-name" data-testid="avatar-agent-name">{agentName || 'Estatel AI User'}</div>
            {agentEmail && <div className="eiq-avatar-email" data-testid="avatar-agent-email">{agentEmail}</div>}
          </div>
          <button type="button"
                  className="eiq-avatar-item"
                  onClick={() => handle(onGoToSettings)}
                  data-testid="avatar-goto-settings">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 10v6m11-11h-6M7 12H1m15.5-6.5l-4.24 4.24M7.76 16.24l-4.24 4.24m12.72 0l-4.24-4.24M7.76 7.76L3.52 3.52"/></svg>
            Settings
          </button>
          {typeof onGoToAdmin === 'function' && (
            <button type="button"
                    className="eiq-avatar-item"
                    onClick={() => handle(onGoToAdmin)}
                    data-testid="avatar-goto-admin">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Owner Admin
            </button>
          )}
          {typeof onOpenSupport === 'function' && (
            <button type="button"
                    className="eiq-avatar-item"
                    onClick={() => handle(onOpenSupport)}
                    data-testid="avatar-open-support">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              Support
            </button>
          )}
          <div className="eiq-avatar-divider" />
          <button type="button"
                  className="eiq-avatar-item eiq-avatar-item-danger"
                  onClick={() => handle(onSignOut || (() => window.alert('Sign-out: clear your session in the host app.')))}
                  data-testid="avatar-signout">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
